export function formatUserDisplayName(user: { name: string | null; email: string }): string {
  return user.name ?? user.email;
}

export function formatShortId(value: string): string {
  const [firstPart] = value.split('-');
  return firstPart || value;
}

export function formatDateTime(value: string | null): string {
  if (!value) {
    return 'Never';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

export interface RelativeLastSeen {
  value: string;
  suffix: string | null;
}

export function formatRelativeLastSeen(value: string | null, now = new Date()): RelativeLastSeen {
  if (!value) {
    return { value: 'Never', suffix: null };
  }

  const lastSeenAt = new Date(value);
  if (Number.isNaN(lastSeenAt.getTime())) {
    return { value: 'Never', suffix: null };
  }

  const secondsAgo = Math.max(0, Math.floor((now.getTime() - lastSeenAt.getTime()) / 1000));

  if (secondsAgo < 60) {
    return { value: formatRelativeUnit(secondsAgo, 'second'), suffix: 'ago' };
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return { value: formatRelativeUnit(minutesAgo, 'minute'), suffix: 'ago' };
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return { value: formatRelativeUnit(hoursAgo, 'hour'), suffix: 'ago' };
  }

  return { value: formatRelativeUnit(Math.floor(hoursAgo / 24), 'day'), suffix: 'ago' };
}

function formatRelativeUnit(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

function maskEmailPart(value: string): string {
  if (value.length <= 3) {
    return value;
  }

  return `${value.slice(0, 3)}${'*'.repeat(Math.min(value.length - 3, 6))}`;
}

export function maskEmailAddress(value: string): string {
  const atIndex = value.indexOf('@');

  if (atIndex <= 0 || atIndex === value.length - 1) {
    return '***';
  }

  const localPart = value.slice(0, atIndex);
  const domainPart = value.slice(atIndex + 1);
  const domainLabels = domainPart.split('.');

  if (domainLabels.length < 2) {
    return `${maskEmailPart(localPart)}@${maskEmailPart(domainPart)}`;
  }

  const topLevelDomain = domainLabels.pop();
  const maskedDomain = domainLabels.map(maskEmailPart).join('.');

  return `${maskEmailPart(localPart)}@${maskedDomain}.${topLevelDomain}`;
}
